import { DateRange }                   from 'rewire-common';
import {
  IFormContext,
  Form,
  validator,
  field,
  error
}                                      from 'rewire-ui';
import { ITimeOffType }                from '../DataTypes';
import DataMutations                   from '../graphql/Mutations';
import DataQueries                     from '../graphql/Queries';
import mainPageModel                   from '../main-navigation/MainPageModel';
import EditDialogModel                 from '../schedule/EditDialogModel';
import { TimeOffTypeSchema }           from '../schedule/TimeOffTypeDialogModel';
import { getAdvancedFormAutoComplete } from '../ui/AdvancedAutoComplete';
import { getNameSearcher }             from '../utility/Searchers';

const employeeSearcher    = getNameSearcher('employees');
const timeOffTypeSearcher = getNameSearcher('timeOffTypes');

const Schema: string = `
  id
  name
`;

export class AccrualDialogModel extends EditDialogModel {
  constructor() {
    super(
      'Accrual',
      (ids: string[]) => DataQueries.getTimeOffAccrualsByIds(ids),
      DataMutations.insertTimeOffAccruals,
      DataMutations.updateTimeOffAccruals,
      DataMutations.deleteTimeOffAccruals,
      undefined,
      () => mainPageModel.canAddTimeOffAccruals,
      () => mainPageModel.canEditTimeOffAccruals,
      () => mainPageModel.canDeleteTimeOffAccruals,
    );
  }

  get accrualActions() {
    return this.isNew ? ['cancel', 'save'] : ['cancel', 'delete', 'save'];
  }

  isTrackingUnits(timeOffType?: ITimeOffType) {
    return !!(timeOffType?.trackingUnits);
  }

  isTrackingAmounts(timeOffType?: ITimeOffType) {
    return !!(timeOffType?.trackingAmounts);
  }

  handleTimeOffTypeChange() {
    this.loadTimeOffType(this.form.field.timeOffType.value.id);
  }

  async loadTimeOffType(timeOffTypeId: string) {
    const results: any                                = await DataQueries.getTimeOffTypesByIds(TimeOffTypeSchema, [timeOffTypeId]);
    this.form.field.timeOffType.value.trackingAmounts = results.length && results[0].trackingAmounts;
    this.form.field.timeOffType.value.trackingUnits   = results.length && results[0].trackingUnits;
  }

  protected fields(form: IFormContext) {
    const start = form.date().label('Start Date');
    const end   = form.date().label('End Date').validators(['required', validator('>', field('start'), error('activity end must be greater than activity start'))]);

    return ({
      employee:        getAdvancedFormAutoComplete(form, employeeSearcher).label('Employee').validators('required'),
      employeeName:    form.static().label('Employee'),
      timeOffType:     form.select(timeOffTypeSearcher).label('Time Off Type').validators('required').onValueChange(() => this.handleTimeOffTypeChange()),
      timeOffTypeName: form.static().label('Time Off Type'),
      start,
      end,
      units:  form.number().label('Units').disabled(()  => !this.isTrackingUnits(this.form.field.timeOffType.value)).validators('required', validator('>', 0)),
      amount: form.number().label('Amount').disabled(() => !this.isTrackingAmounts(this.form.field.timeOffType.value)).validators('required', validator('>', 0)),
    });
  }

  protected _createForm(): Form {
    return Form.create((form: IFormContext) => ({
      ...this.fields(form)
    }));
  }

  async lookupFormData(employeeId?: string, timeOffTypeId?: string): Promise<any> {
    const promises: any[]          = [2];
    if (employeeId)    promises[0] = DataQueries.getEmployeesByIds(Schema, [employeeId]);
    if (timeOffTypeId) promises[1] = DataQueries.getTimeOffTypesByIds(TimeOffTypeSchema, [timeOffTypeId]);
    const results                  = await Promise.all(promises);
    return { employee: employeeId && results[0]?.[0], timeOffType: timeOffTypeId && results[1]?.[0] };
  }

  initializeFields(isNew: boolean): void {
    this.form.field.employeeName.visible    = !isNew;
    this.form.field.timeOffTypeName.visible = !isNew;
    this.form.field.employee.visible        = isNew;
    this.form.field.timeOffType.visible     = isNew;

    this.form.field.employee.disabled       = () => !isNew;
    this.form.field.timeOffType.disabled    = () => !isNew;
  }

  async createAccrual(employeeId?: string, timeOffTypeId?: string) {
    this.state.enable                       = true;
    this.initializeFields(true);
    await super.load(undefined, true);
    const lookup                            = await this.lookupFormData(employeeId, timeOffTypeId);
    this.form.field.employee.value          = lookup.employee;
    this.form.field.timeOffType.value       = lookup.timeOffType;
  }

  async editAccrual(accrualId: string, _employeeId: string, _timeOffTypeId: string) {
    this.state.enable                       = true;
    this.initializeFields(false);
    await super.load(accrualId, true);
    this.form.field.employeeName.value      = this.form.value.employee.name;
    this.form.field.timeOffTypeName.value   = this.form.value.timeOffType.name;
    this.form.field.start.value             = this.form.value.effective.start;
    this.form.field.end.value               = this.form.value.effective.end;
  }

  mapToSave(): ObjectType {
    const obj         = super.mapToSave();

    if (this.isNew) {
      obj.employeeId    = obj.employee.id;
      obj.timeOffTypeId = obj.timeOffType.id;
    }
    obj.effective = (new DateRange(obj.start, obj.end)).toString();
    obj.amount    = obj.amount || 0;
    obj.units     = obj.units  || 0;
    delete obj.employeeName;
    delete obj.timeOffTypeName;
    delete obj.employee;
    delete obj.timeOffType;
    delete obj.start;
    delete obj.end;
    return obj;
  }
}

const accrualDialogModel = new AccrualDialogModel();
export default accrualDialogModel;
